import React, { useContext, useState, useEffect } from 'react';
import { Button, Card, Typography, Space, List, Tag, Divider, message } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { GlobalContext } from '../../App';
import * as turf from '@turf/turf';
import { Path, StaticGoogleMap } from '../google-js-maps/StaticGoogleMap';

const { Title, Text } = Typography;

const InvestigateSidebar = ({ onShowHistory, isLoading, onShowHistoricalData, historyData }) => {
  const { investigatePolygon } = useContext(GlobalContext);
  const [historyItems, setHistoryItems] = useState([]);

  useEffect(() => {
    const loadHistory = () => {
      const savedHistory = JSON.parse(localStorage.getItem('InvestigateHistory') || '[]');
      setHistoryItems(savedHistory);
    };
    
    loadHistory();
    
    // Listen for history updates
    window.addEventListener('investigateHistoryUpdated', loadHistory);
    
    return () => {
      window.removeEventListener('investigateHistoryUpdated', loadHistory);
    };
  }, []);

  const getPolygonArea = (polygon) => {
    if (!polygon || !polygon.geometry) return null;
    try {
      const area = turf.area(polygon);
      return (area / 10000).toFixed(2); // Convert to hectares
    } catch (error) {
      return null;
    }
  };

  const getPolygonPathPoints = (polygon) => {
    if (!polygon || !polygon.geometry || !polygon.geometry.coordinates) return null;
    try {
      const coords = polygon.geometry.type === 'MultiPolygon'
        ? polygon.geometry.coordinates[0][0] : polygon.geometry.coordinates[0];
      return coords.map((coord) => ({ lat: coord[1], lng: coord[0] }));
    } catch (error) {
      return null;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleCopyData = async () => {
    if (!historyData) {
      message.error('No data to copy');
      return;
    }
    
    try {
      await navigator.clipboard.writeText(JSON.stringify(historyData, null, 2));
      message.success('Data copied to clipboard');
    } catch (error) {
      message.error('Failed to copy data');
    }
  };

  return (
    <div style={{ padding: '16px', height: '100%', overflow: 'auto' }}>
      <Card size="small" style={{ marginBottom: '16px' }}>
        <Title level={4}>AOI Investigation</Title>
        <Text type="secondary">
          Draw a polygon on the map to analyze 5-year Sentinel-2 satellite history
        </Text>
      </Card>

      {/* Current Polygon Section */}
      <Card size="small" style={{ marginBottom: '16px' }}>
        <Title level={5}>Current Polygon</Title>
        {investigatePolygon ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            {getPolygonPathPoints(investigatePolygon) ? (
              <StaticGoogleMap
                size="300x120"
                maptype="satellite"
                apiKey={process.env.REACT_APP_GOOGLE_MAPS_KEY}
                style={{ width: '100%', height: 120, borderRadius: 6 }}
              >
                <Path
                  weight="3"
                  color="0x1890ff"
                  fillcolor="0x1890ff33"
                  points={getPolygonPathPoints(investigatePolygon)}
                />
              </StaticGoogleMap>
            ) : (
              <div style={{ 
                width: '100%', 
                height: '120px', 
                background: '#f0f0f0', 
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px dashed #1890ff'
              }}>
                <Text type="secondary">Polygon Preview</Text>
              </div>
            )}
            <Space>
              <Tag color="blue">Area: {getPolygonArea(investigatePolygon)} ha</Tag>
              <Tag color="green">Ready</Tag>
            </Space>
          </Space>
        ) : (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            background: '#fafafa',
            borderRadius: '6px',
            border: '1px dashed #d9d9d9'
          }}>
            <Text type="secondary">Draw a polygon on the map</Text>
          </div>
        )}
      </Card>

      <Space direction="vertical" style={{ width: '100%', marginBottom: '24px' }}>
        <Button 
          type="primary" 
          block 
          onClick={onShowHistory}
          disabled={!investigatePolygon}
          loading={isLoading}
        >
          {isLoading ? 'Analyzing...' : 'Show History'}
        </Button>
        
        <Button 
          block 
          onClick={handleCopyData}
          disabled={!historyData}
        >
          Copy All Data
        </Button>
      </Space>

      <Divider />

      {/* History Section */}
      <Card size="small">
        <Title level={5}>Analysis History</Title>
        {historyItems.length > 0 ? (
          <List
            size="small"
            dataSource={historyItems}
            renderItem={(item, index) => (
              <List.Item style={{ padding: '8px 0' }}>
                <Card size="small" style={{ width: '100%' }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {getPolygonPathPoints(item.polygon) ? (
                      <StaticGoogleMap
                        size="300x80"
                        maptype="satellite"
                        apiKey={process.env.REACT_APP_GOOGLE_MAPS_KEY}
                        style={{ width: '100%', height: 80, borderRadius: 4 }}
                      >
                        <Path
                          weight="3"
                          color="0x1890ff"
                          fillcolor="0x1890ff33"
                          points={getPolygonPathPoints(item.polygon)}
                        />
                      </StaticGoogleMap>
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '80px',
                        background: '#f0f0f0',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>Analysis #{historyItems.length - index}</Text>
                      </div>
                    )}
                    <Space size="small" wrap style={{ justifyContent: 'space-between', width: '100%' }}>
                      <Space size="small" wrap>
                        <Tag size="small" color="blue">{getPolygonArea(item.polygon)} ha</Tag>
                        <Tag size="small" color="green">{formatDate(item.analyzedAt)}</Tag>
                      </Space>
                      <Button 
                        type="text" 
                        size="small" 
                        icon={<RightOutlined />}
                        onClick={() => {
                          console.log('Arrow clicked, item:', item);
                          onShowHistoricalData && onShowHistoricalData(item.data, item.polygon);
                        }}
                        style={{ 
                          color: '#1890ff',
                          padding: '0 4px'
                        }}
                      />
                    </Space>
                  </Space>
                </Card>
              </List.Item>
            )}
          />
        ) : (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            background: '#fafafa',
            borderRadius: '6px'
          }}>
            <Text type="secondary">No analysis history yet</Text>
          </div>
        )}
      </Card>
    </div>
  );
};

export default InvestigateSidebar;
